import { Badge } from '@abyss/web/ui/Badge';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getProviderTitle } from '../../../../common/Utils';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  GetCareButton,
  NetworkWrapper,
  VirtualCareListViewCardBottomContent,
  VirtualCareListViewCardContent,
  VirtualCareListViewContainer,
  getCareTextStyle,
  providerImageStyle,
  virtualCareOnlyTextStyle,
  virtualCareProviderNameTextStyle,
  virtualCareServiceTypeTextStyle,
} from './VirtualCareListCard.styled';
import { VirtualCareProviderModal } from './VirtualCareProvidersBanner/VirtualCareProvidersModal';

interface Provider {
  providerName: string;
  logo?: string;
  link: string;
  linkType: string;
}

interface VirtualCareListCardProps {
  provider: Provider;
  serviceType: string;
  mobileScreen: boolean;
  virtualCareProviders: any;
}

const VirtualCareListCard: React.FC<VirtualCareListCardProps> = ({
  provider,
  serviceType,
  mobileScreen,
  virtualCareProviders,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const uiMessaging = useLagoon('ui-messaging')();
  const { t } = useTranslation();

  const lagoonData = useLagoon('ui-messaging')();

  const vcCostEstimateMessage = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.VIRTUAL_CARE_COST_ESTIMATE_MESSAGE,
  });

  const onClickHandler = useCallback(
    (e) => {
      e.preventDefault();
      setIsModalOpen(true);
    },
    [provider, setIsModalOpen]
  );

  const avatarElement = useMemo(
    () =>
      provider?.logo ? (
        <img
          alt={`${provider?.providerName} logo`}
          src={provider?.logo}
          style={providerImageStyle(mobileScreen)}
        />
      ) : (
        <Badge
          css={{
            '&.abyss-badge-root': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: mobileScreen ? '85px' : '150px',
              height: mobileScreen ? '22px' : '40px',
              borderRadius: '4px',
              padding: '8px',
              margin: '0px',
              textWrap: 'balance',
              fontSize: mobileScreen ? '10px' : '11.25px',
              whiteSpace: 'nowrap',
            },
          }}
          variant="neutral"
        >
          {t('Logo not available')}
        </Badge>
      ),
    [mobileScreen, provider?.logo, provider?.providerName]
  );

  const InNetWorkBadge = () => (
    <NetworkWrapper>
      <IconSymbol
        color="$success1"
        icon="check_circle"
        size={mobileScreen ? '16' : '20'}
      />
      <Text css={{ fontSize: mobileScreen ? '14px' : '16px' }}>
        {`${t('VIRTUAL_CARE_PROVIDERS_BANNER.IN_NETWORK')}`}
      </Text>
    </NetworkWrapper>
  );

  const avatarAndNetworkSection = useMemo(
    () => (
      <Flex
        alignItems="center"
        css={{ gap: mobileScreen ? '4px' : '16px' }}
        data-testid="virtual-care-list-view-card-left-section"
        direction="column"
        justify="center"
      >
        {avatarElement}
        <InNetWorkBadge />
      </Flex>
    ),
    [avatarElement, mobileScreen]
  );

  const NameAndSpecialitySection = () => (
    <>
      <Link
        css={virtualCareProviderNameTextStyle(mobileScreen)}
        data-auto-testid={`virtual-care-${provider?.providerName}-link-text`}
        data-testid={`virtual-care-${provider?.providerName}-link-text`}
        onClick={onClickHandler}
      >
        {provider?.providerName}
      </Link>
      <Text css={virtualCareServiceTypeTextStyle(mobileScreen)}>
        {getProviderTitle(serviceType, uiMessaging, provider)}
      </Text>
    </>
  );

  const YouPaySection = () => (
    <Flex
      alignItems={mobileScreen ? 'flex-start' : 'center'}
      css={{
        gap: '8px',
        color: '#323333',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        fontSize: mobileScreen ? '14px' : '16px',
        lineHeight: mobileScreen ? '18px' : '20px',
      }}
      direction={mobileScreen ? 'row' : 'column'}
      justify="center"
    >
      <Text
        css={{
          fontFamily: 'UHC2020 Sans',
          fontWeight: '600',
        }}
      >
        {t('VIRTUAL_CARE_PROVIDERS_BANNER.YOU_PAY')}
      </Text>
      <Flex
        alignItems="center"
        css={{ gap: mobileScreen ? '4px' : '8px' }}
        direction="row"
        justify="center"
      >
        <Text
          css={{
            fontWeight: '500',
          }}
        >
          {t('VIRTUAL_CARE_PROVIDERS_BANNER.VARIES')}
        </Text>
        <Popover
          content={vcCostEstimateMessage?.message}
          data-testid="virtual-care-list-cost-estimate-popover"
          onOpenChange={() => setIsPopoverOpen(!isPopoverOpen)}
          open={isPopoverOpen}
          title={`${t('Cost estimate')}`}
        >
          <IconSymbol
            color="#196ECF"
            data-auto-testid="virtual-care-list-cost-estimate-popover-icon"
            data-testid="virtual-care-list-cost-estimate-popover-icon"
            icon="info"
            onClick={() => setIsPopoverOpen(!isPopoverOpen)}
            size="18"
            variant="outlined"
          />
        </Popover>
      </Flex>
    </Flex>
  );

  const VirtualCareOnlySection = () => (
    <Flex
      alignItems="center"
      css={{ gap: '4px' }}
      data-testid="virtual-care-list-view-card-virtual-care-only-section"
      direction="row"
      justify="space-between"
    >
      <IconSymbol color="$gray8" icon="videocam" variant="outlined" />
      <Text css={virtualCareOnlyTextStyle(mobileScreen)}>
        {t('VIRTUAL_CARE_PROVIDERS_BANNER.VIRTUAL_CARE_ONLY')}
      </Text>
    </Flex>
  );

  const GetCareSection = () => (
    <GetCareButton
      data-testid="virtual-care-list-get-care-button"
      onClick={onClickHandler}
      variant="outline"
    >
      <Text css={getCareTextStyle()}>
        {t('VIRTUAL_CARE_PROVIDERS_BANNER.GET_CARE')}
      </Text>
      <IconSymbol icon="open_in_new" size="18" />
    </GetCareButton>
  );

  return (
    <>
      {!mobileScreen && (
        <VirtualCareListViewContainer data-testid="virtual-care-list-view-container">
          <Flex
            alignItems="flex-end"
            css={{ alignSelf: 'stretch' }}
            data-testid="virtual-care-list-view-card-container"
            direction="column"
          >
            <VirtualCareListViewCardContent data-testid="virtual-care-list-view-card-content">
              {avatarAndNetworkSection}
              <Flex
                alignItems="flex-start"
                css={{ gap: '16px', width: '100%', maxWidth: '572px' }}
                data-testid="virtual-care-list-view-card-center-section"
                direction="column"
              >
                <Flex
                  alignItems="flex-start"
                  css={{ gap: '4px', width: '100%' }}
                  data-testid="virtual-care-list-view-card-provider-name-section"
                  direction="column"
                >
                  <NameAndSpecialitySection />
                </Flex>
                <VirtualCareOnlySection />
              </Flex>
              <Flex
                alignItems="center"
                css={{
                  paddingRight: '32px',
                  gap: '16px',
                  width: '100%',
                  maxWidth: '398px',
                }}
                data-testid="virtual-care-list-view-card-right-section"
                justify="flex-end"
              >
                <YouPaySection />
              </Flex>
            </VirtualCareListViewCardContent>
            <VirtualCareListViewCardBottomContent>
              <GetCareSection />
            </VirtualCareListViewCardBottomContent>
          </Flex>
        </VirtualCareListViewContainer>
      )}

      {mobileScreen && (
        <VirtualCareListViewContainer data-testid="virtual-care-list-view-container">
          <Flex
            alignItems="flex-end"
            css={{ width: '100%', alignSelf: 'stretch', gap: '16px' }}
            data-testid="virtual-care-list-view-card-container"
            direction="column"
          >
            <Flex
              alignItems="center"
              css={{ gap: '16px', width: '100%' }}
              direction="row"
              justify="center"
            >
              {avatarAndNetworkSection}
              <Flex
                alignItems="flex-start"
                css={{ gap: '8px', flex: '1 0 0' }}
                direction="column"
              >
                <Flex direction="column">
                  <NameAndSpecialitySection />
                </Flex>
                <VirtualCareOnlySection />
                <YouPaySection />
              </Flex>
            </Flex>
            <GetCareSection />
          </Flex>
        </VirtualCareListViewContainer>
      )}
      <Divider color="#E5E5E6" margin={0} orientation="horizontal" />
      <VirtualCareProviderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedProviderName={provider?.providerName}
        virtualCareProviders={virtualCareProviders}
      />
    </>
  );
};

export default VirtualCareListCard;
